// IRON CONDOR · one candidate (read-only): the four legs at their depth prices, every Phase 1 figure,
// the broker's basket margin (read-only calculator) and the theoretical expiry payoff.
import { BE_DISTANCE_NOTE, beDistanceBeyond, beDistanceText } from "@/lib/breakeven";
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { pct, rupees } from "@/components/options/PairValuation";
import { age, px, qty } from "@/components/options/OptionsCommon";
import IcPayoffChart from "@/components/iron_condor/IcPayoffChart";

const sideCls = (s) => (s === "BUY" ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FF3B30]/15 text-[#FF3B30]");
const unit = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

export function Tile({ label, value, sub, testId, tone }) {
  const cls = tone === "good" ? "text-[#00C805]" : tone === "bad" ? "text-[#FF3B30]" : "";
  return (
    <div className="term-panel-2 rounded-sm px-2 py-1.5 min-w-[7.5rem]" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]">{label}</div>
      <div className={`text-sm font-bold mono ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-[var(--term-muted)] mono">{sub}</div>}
    </div>
  );
}

export function marginText(cap) {
  if (!cap) return "—";
  if (cap.status === "OK" || cap.status === "STALE") return rupees(cap.capital_required);
  if (cap.status === "PENDING") return "pending";
  return cap.status === "DISABLED" ? "off" : cap.status;
}

export default function IcDetail({ id, onClose, onOpenBuilder }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setD(null); setErr(null); }, [id]);
  useEffect(() => {
    if (!id) return undefined;
    const load = () => optionsApi.get(`/ic/candidates/${encodeURIComponent(id)}`, { params: { points: 80 } })
      .then((r) => { setD(r.data); setErr(null); }).catch((e) => setErr(optionsError(e)));
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 2000);
    return () => clearInterval(t);
  }, [id]);
  if (!id) return null;
  const m = d?.metrics || {};
  const cap = d?.capital;
  const money = (r, u) => (r !== null && r !== undefined ? rupees(r) : `${unit(u)} / unit`);
  const fetchMargin = async () => {
    setBusy(true);
    try { const r = await optionsApi.post(`/ic/candidates/${encodeURIComponent(id)}/margin`); setD((x) => ({ ...x, capital: r.data.capital })); }
    catch (e) { toast.error(optionsError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-[860px] max-w-full h-full overflow-auto bg-[var(--term-bg)] border-l border-[var(--term-border-strong)] p-4 space-y-3"
        onClick={(e) => e.stopPropagation()} data-testid="ic-detail">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold">{d ? `${d.underlying} · ${d.exchange} · ${d.expiry}` : "Iron Condor"}</h2>
          {d && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[var(--term-s3)] mono">{d.source}</span>}
          {d?.stale && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FFCC00]/20 text-[#b38f00] font-bold">STALE QUOTES</span>}
          <button className="ml-auto p-1 hover:bg-[var(--term-s3)] rounded-sm" onClick={onClose} aria-label="Close" data-testid="ic-detail-close"><X className="h-4 w-4" /></button>
        </div>
        {err && <div className="text-xs text-[#FF3B30]">{err}</div>}
        {d && (<>
          <div className="text-[11px] text-[var(--term-secondary)]">
            Spot {px(d.spot)} ({d.underlying_symbol}, {d.underlying_rule}) · {d.lots} lot{d.lots > 1 ? "s" : ""} × {qty(d.lot_size)} = {qty(d.quantity)} per leg
            {d.units_verified ? ` · ${d.units} price unit(s) per quantity` : " · broker units not yet verified (₹ figures withheld)"}
            {" "}· oldest quote {age(d.quote_age_s)} · read-only analysis, no orders
          </div>
          <table className="w-full text-xs border-collapse" data-testid="ic-detail-legs">
            <thead className="text-[var(--term-secondary)] bg-[var(--term-s3)]">
              <tr>{["Leg", "Side", "Type", "Strike", "Symbol", "Bid", "Ask", "Depth price", "Limit", "OI", "Volume"].map((h) =>
                <th key={h} className="px-1.5 py-1.5 text-right first:text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.legs.map((l) => (
                <tr key={l.leg} className="border-t border-[var(--term-border)] mono">
                  <td className="px-1.5 py-1">L{l.leg}</td>
                  <td className="px-1.5 text-right"><span className={`px-1.5 rounded-sm text-[10px] font-bold ${sideCls(l.side)}`}>{l.side}</span></td>
                  <td className="px-1.5 text-right"><span className="px-1.5 rounded-sm text-[10px] font-bold bg-[#2563EB]/15 text-[#2563EB]">{l.option_type}</span></td>
                  <td className="px-1.5 text-right font-bold">{l.strike}</td>
                  <td className="px-1.5 text-right">{l.tradingsymbol}</td>
                  <td className="px-1.5 text-right">{px(l.bid)}</td>
                  <td className="px-1.5 text-right">{px(l.ask)}</td>
                  <td className="px-1.5 text-right font-bold">{unit(l.price)}</td>
                  <td className="px-1.5 text-right">{px(l.limit)}</td>
                  <td className="px-1.5 text-right">{qty(l.oi)}</td>
                  <td className="px-1.5 text-right">{qty(l.volume)}</td>
                </tr>))}
            </tbody>
          </table>
          <div className="text-[10px] text-[var(--term-muted)]">SELL legs priced at the VWAP of the bids their quantity takes, BUY legs at the offers (indicative entry, never the LTP). Limit = worst level touched.</div>
          <div className="flex flex-wrap gap-2" data-testid="ic-detail-metrics">
            <Tile label="Net credit" value={money(m.net_credit, m.net_credit_unit)} sub={`${unit(m.net_credit_unit)} / unit`} tone="good" testId="ic-tile-credit" />
            <Tile label="Max profit" value={money(m.max_profit, m.max_profit_unit)} sub={m.max_profit_net != null ? `net of est. charges ${rupees(m.max_profit_net)}` : null} tone="good" />
            <Tile label="Max loss" value={money(m.max_loss, m.max_loss_unit)} sub={m.max_loss_net != null ? `incl. est. charges ${rupees(m.max_loss_net)}` : null} tone="bad" testId="ic-tile-maxloss" />
            <Tile label="Breakevens · expiry" value={`${m.breakeven_low != null ? Number(m.breakeven_low).toFixed(2) : "none"} – ${m.breakeven_high != null ? Number(m.breakeven_high).toFixed(2) : "none"}`}
              testId="ic-tile-breakevens" sub={`${beDistanceText(d.spot, m.breakeven_low, m.breakeven_high) || "no spot"} from spot${d.stale ? " · stale" : ""} · not a stop-loss`} />
            <Tile label="Put / Call width" value={`${m.put_width} / ${m.call_width}`} sub={m.symmetric ? "symmetric" : "asymmetric"} />
            <Tile label="Put / Call side loss" value={`${money(m.put_side_loss, m.put_side_loss_unit)} / ${money(m.call_side_loss, m.call_side_loss_unit)}`} />
            <Tile label="Return on risk" value={pct(m.return_on_risk_pct)} sub={`risk : reward ${m.risk_reward}`} />
            <Tile label="Credit / width" value={pct(m.credit_to_width_pct)} />
            <Tile label="Est. charges" value={m.costs != null ? rupees(m.costs) : "—"} sub="open + close, estimate" />
            <Tile label="Broker margin" value={marginText(cap)} testId="ic-tile-margin"
              sub={cap?.return_on_margin_pct != null ? `return on margin ${pct(cap.return_on_margin_pct)}${cap.status === "STALE" ? " (stale)" : ""}` : cap?.error || "Zerodha basket calculator"} />
          </div>
          {onOpenBuilder && <button type="button" onClick={() => onOpenBuilder(d)} data-testid="ic-open-builder"
            className="h-7 px-3 mr-2 rounded-sm text-[11px] font-bold bg-[#2563EB] text-white">Open in Strategy Builder</button>}
          <button type="button" disabled={busy} onClick={fetchMargin} data-testid="ic-margin-refresh"
            className="h-7 px-3 rounded-sm text-[11px] font-bold border border-[var(--term-border-strong)] hover:border-[#2563EB] disabled:opacity-40">
            {busy ? "Asking Zerodha…" : "Get broker margin (read-only calculator)"}</button>
          <div className="term-panel rounded-sm p-2"><IcPayoffChart payoff={d.payoff} /></div>
          <div className="text-[10px] text-[var(--term-muted)]">Model Greeks and probability of profit: open it in the Strategy Builder. Nothing here places or prepares an order.</div>
        </>)}
      </div>
    </div>
  );
}
